import { useCallback, useEffect, useRef, useState } from 'react';

import { Box, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import { MarketplacePropertyCategory_ListingMediaState as ListingMediaState } from '@opendoor/observability/pbs/journey/data/categories/marketplace_property_category_pb';
import { IconX } from '@tabler/icons-react';
import { createPortal } from 'react-dom';

import type { PdpMediaSlot } from './usePdpMediaSlot';

type HeroTour3dProps = {
  tourUrl: string;
  addressLabel: string | null;
  slot: PdpMediaSlot;
  trigger: 'fill' | 'control';
};

export const HeroTour3d = ({ tourUrl, addressLabel, slot, trigger }: HeroTour3dProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const restoreFocus = useRef(false);

  const handleOpen = useCallback(() => {
    restoreFocus.current = true;
    slot.selectMediaState(ListingMediaState.TOUR_3D);
    setIsOpen(true);
  }, [slot]);

  const handleClose = useCallback(() => {
    slot.selectMediaState(ListingMediaState.PHOTOS);
    setIsOpen(false);
  }, [slot]);

  const stopPointerPropagation = useCallback((event: { stopPropagation: () => void }) => {
    event.stopPropagation();
  }, []);

  const handleCancel = useCallback(
    (event: { preventDefault: () => void }) => {
      event.preventDefault();
      handleClose();
    },
    [handleClose],
  );

  useEffect(() => {
    if (!isOpen) {
      if (restoreFocus.current) {
        restoreFocus.current = false;
        triggerRef.current?.focus({ preventScroll: true });
      }
      return undefined;
    }

    const dialog = dialogRef.current;
    dialog?.showModal();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus({ preventScroll: true });
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        handleClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown, true);
      dialog?.close();
    };
  }, [handleClose, isOpen]);

  if (slot.assignedState !== ListingMediaState.TOUR_3D) {
    return null;
  }

  const addressSuffix = addressLabel ? ` of ${addressLabel}` : '';
  const embedUrl = `${tourUrl}?setAttribution=mls&wl=true&initialViewType=pano&utm_source=dashboard`;
  const viewer =
    isOpen && typeof document !== 'undefined'
      ? createPortal(
          <dialog
            ref={dialogRef}
            aria-modal="true"
            aria-label={`3D tour${addressSuffix}`}
            onCancel={handleCancel}
            className="fixed inset-0 z-[20000] m-0 flex size-full max-h-none max-w-none flex-col border-0 bg-od-bg-inverse-primary p-0"
          >
            <Row className="flex-wrap items-center justify-between gap-3 px-4 py-3 md:px-6">
              <Text as="h2" className="text-title-sm text-od-content-inverse-primary">
                3D Tour
              </Text>
              <Row className="items-center gap-2">
                <Button
                  analyticsName="marketplace-pdp-tour-3d-new-tab"
                  aria-label="Open 3D Tour in a new tab"
                  href={tourUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="secondaryInverse"
                  size="sm"
                >
                  Open 3D Tour in a new tab
                </Button>
                <Button
                  ref={closeRef}
                  analyticsName="marketplace-pdp-tour-3d-close"
                  aria-label="Close 3D tour and return to photos"
                  onClick={handleClose}
                  variant="secondaryInverse"
                  className="size-9 rounded-full bg-transparent p-0"
                >
                  <IconX size={24} />
                </Button>
              </Row>
            </Row>
            <Box className="min-h-0 flex-1 px-3 pb-3 md:px-6 md:pb-6">
              {/* oxlint-disable react/iframe-missing-sandbox -- Zeus restricts this URL to cross-origin Zillow; its challenge needs scripts and its provider origin. */}
              <iframe
                src={embedUrl}
                title={`3D tour${addressSuffix}`}
                allow="fullscreen; xr-spatial-tracking"
                allowFullScreen
                sandbox="allow-forms allow-popups allow-popups-to-escape-sandbox allow-same-origin allow-scripts"
                className="size-full rounded-xl border-0 bg-od-bg-primary"
              />
              {/* oxlint-enable react/iframe-missing-sandbox */}
            </Box>
          </dialog>,
          document.body,
        )
      : null;

  return (
    <>
      <Button
        ref={triggerRef}
        analyticsName="marketplace-pdp-tour-3d-open"
        aria-label={`Open 3D tour${addressSuffix}`}
        onClick={handleOpen}
        onPointerDown={stopPointerPropagation}
        variant="secondary"
        className={cn(
          'pointer-events-auto absolute z-[2] border-0 p-0',
          trigger === 'fill'
            ? 'inset-0 size-full items-end justify-start rounded-none bg-transparent p-5'
            : 'bottom-5 left-6 h-12 rounded-xl px-4 shadow-md',
        )}
      >
        <Text
          as="span"
          className={cn(
            'text-label-md',
            trigger === 'fill' &&
              'rounded-xl bg-od-bg-primary px-4 py-3 text-od-content-primary shadow-md',
          )}
        >
          3D Tour
        </Text>
      </Button>
      {viewer}
    </>
  );
};
